import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const ALL_FEATURES = [
  'dashboard', 'sales', 'inventory', 'walkins', 'followups',
  'collaterals', 'reports', 'channel-partners', 'eoi',
  'construction', 'projects', 'intelligence', 'admin', 'access-control'
];

const ALL_ROLES = [
  'super_admin', 'admin', 'director', 'sales_head', 'sales', 'sales_coordinator', 'receptionist', 'viewer'
];

const ROLE_LABELS = {
  super_admin: '👑 Super Admin',
  admin: '⚙️ Admin',
  director: '🏢 Director',
  sales_head: '📊 Sales Head',
  sales: '💰 Sales',
  sales_coordinator: '📋 Coordinator',
  receptionist: '🚪 Receptionist',
  viewer: '👁️ Viewer'
};

const PERM_KEYS = ['can_view', 'can_create', 'can_edit', 'can_delete', 'can_view_phone', 'can_view_name', 'can_export'];
const PERM_LABELS = {
  can_view: '👁', can_create: '➕', can_edit: '✏️', can_delete: '🗑',
  can_view_phone: '📱', can_view_name: '👤', can_export: '📥'
};
const PERM_TOOLTIPS = {
  can_view: 'View', can_create: 'Create', can_edit: 'Edit', can_delete: 'Delete',
  can_view_phone: 'View Phone', can_view_name: 'View Name', can_export: 'Export'
};

const ROLE_COLORS = {
  super_admin: '#7c3aed', admin: '#2563eb', director: '#059669', sales_head: '#d97706',
  sales: '#0891b2', sales_coordinator: '#6366f1', receptionist: '#ec4899', viewer: '#6b7280'
};

const AccessControl = ({ apiBase, user }) => {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [auditLogs, setAuditLogs] = useState([]);
  const [loginLogs, setLoginLogs] = useState([]);
  const [loginRestrictions, setLoginRestrictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'sales', phone: '', designation: '', department: '' });
  const [toast, setToast] = useState(null);
  const [dirtyRoles, setDirtyRoles] = useState(new Set());

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/admin/users`, { headers });
      setUsers(res.data);
    } catch (err) { console.error('Error fetching users:', err); }
  }, [apiBase]);

  const fetchMatrix = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/permissions/roles-matrix`, { headers });
      setMatrix(res.data.matrix || {});
    } catch (err) { console.error('Error fetching matrix:', err); }
  }, [apiBase]);

  const fetchAuditLog = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/permissions/audit-log?limit=200`, { headers });
      setAuditLogs(res.data.logs || []);
    } catch (err) { console.error('Error:', err); }
  }, [apiBase]);

  const fetchLoginLog = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/permissions/login-log?limit=100`, { headers });
      setLoginLogs(res.data || []);
    } catch (err) { console.error('Error:', err); }
  }, [apiBase]);

  const fetchLoginRestrictions = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/permissions/login-restrictions`, { headers });
      setLoginRestrictions(res.data || []);
    } catch (err) { console.error('Error:', err); }
  }, [apiBase]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchUsers(), fetchMatrix()]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (activeTab === 'audit') { fetchAuditLog(); fetchLoginLog(); }
    if (activeTab === 'restrictions') fetchLoginRestrictions();
  }, [activeTab]);

  // ── User Management ──
  const handleAddUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      return showToast('Name, email and password required', 'error');
    }
    setSaving(true);
    try {
      await axios.post(`${apiBase}/admin/users`, newUser, { headers });
      showToast('User created successfully');
      setShowAddUser(false);
      setNewUser({ name: '', email: '', password: '', role: 'sales', phone: '', designation: '', department: '' });
      fetchUsers();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to create user', 'error');
    } finally { setSaving(false); }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      await axios.put(`${apiBase}/admin/users/${editingUser.id}`, editingUser, { headers });
      showToast('User updated successfully');
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to update', 'error');
    } finally { setSaving(false); }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      const endpoint = currentStatus ? 'revoke' : 'activate';
      await axios.post(`${apiBase}/admin/users/${userId}/${endpoint}`, {}, { headers });
      showToast(`User ${currentStatus ? 'revoked' : 'activated'}`);
      fetchUsers();
    } catch (err) { showToast('Failed to update status', 'error'); }
  };

  const handleResetPassword = async (userId) => {
    const newPw = prompt('Enter new password for this user:');
    if (!newPw) return;
    try {
      await axios.post(`${apiBase}/admin/users/${userId}/reset-password`, { newPassword: newPw }, { headers });
      showToast('Password reset successfully');
    } catch (err) { showToast('Failed to reset password', 'error'); }
  };

  // ── Permission Matrix ──
  const togglePerm = (role, feature, permKey) => {
    setMatrix(prev => {
      const updated = { ...prev };
      if (!updated[role]) updated[role] = {};
      if (!updated[role][feature]) updated[role][feature] = {};
      updated[role][feature] = { ...updated[role][feature], [permKey]: !updated[role][feature][permKey] };
      return updated;
    });
    setDirtyRoles(prev => new Set(prev).add(role));
  };

  const toggleAllForRole = (role, value) => {
    setMatrix(prev => {
      const updated = { ...prev };
      updated[role] = {};
      ALL_FEATURES.forEach(f => {
        updated[role][f] = {};
        PERM_KEYS.forEach(k => { updated[role][f][k] = value; });
      });
      return updated;
    });
    setDirtyRoles(prev => new Set(prev).add(role));
  };

  const saveRolePerms = async (role) => {
    setSaving(true);
    try {
      await axios.put(`${apiBase}/permissions/roles-matrix/${role}`, { permissions: matrix[role] || {} }, { headers });
      showToast(`${ROLE_LABELS[role] || role} permissions saved`);
      setDirtyRoles(prev => { const n = new Set(prev); n.delete(role); return n; });
    } catch (err) {
      showToast('Failed to save permissions', 'error');
    } finally { setSaving(false); }
  };

  // ── Login Restrictions ──
  const saveRestriction = async (role, data) => {
    try {
      await axios.put(`${apiBase}/permissions/login-restrictions/${role}`, data, { headers });
      showToast(`Login restrictions for ${role} saved`);
      fetchLoginRestrictions();
    } catch (err) { showToast('Failed to save', 'error'); }
  };

  if (loading) return <div className="card"><div className="loading">Loading Access Control...</div></div>;

  const tabs = [
    { id: 'users', label: '👥 Users', count: users.length },
    { id: 'permissions', label: '🔐 Permissions' },
    { id: 'audit', label: '📋 Audit Trail', count: auditLogs.length },
    { id: 'restrictions', label: '⏰ Login Rules' }
  ];

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: '20px', right: '20px', zIndex: 9999,
          padding: '12px 24px', borderRadius: '8px', color: '#fff', fontWeight: '600',
          background: toast.type === 'error' ? '#ef4444' : '#10b981',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', animation: 'fadeIn 0.3s'
        }}>
          {toast.msg}
        </div>
      )}

      <h1 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        🔐 Access Control & Permissions
      </h1>

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '2px solid #e5e7eb', paddingBottom: '0' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 20px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '600',
              borderBottom: activeTab === tab.id ? '3px solid #2563eb' : '3px solid transparent',
              color: activeTab === tab.id ? '#2563eb' : '#6b7280',
              background: activeTab === tab.id ? '#eff6ff' : 'transparent',
              borderRadius: '8px 8px 0 0', transition: 'all 0.2s'
            }}
          >
            {tab.label}
            {tab.count !== undefined && <span style={{ marginLeft: '6px', background: '#e5e7eb', borderRadius: '10px', padding: '2px 8px', fontSize: '12px' }}>{tab.count}</span>}
          </button>
        ))}
      </div>

      {/* ═══ TAB 1: USER MANAGEMENT ═══ */}
      {activeTab === 'users' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h2 className="card-title" style={{ margin: 0 }}>Team Members</h2>
            <button className="btn btn-primary" onClick={() => setShowAddUser(true)}>+ Add User</button>
          </div>

          {/* Add User Modal */}
          {showAddUser && (
            <div className="card" style={{ marginBottom: '16px', border: '2px solid #2563eb', background: '#f0f9ff' }}>
              <h3 style={{ marginBottom: '12px', fontWeight: '600' }}>Add New User</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                <div className="form-group">
                  <label className="form-label">Name *</label>
                  <input className="form-input" value={newUser.name} onChange={e => setNewUser({ ...newUser, name: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input className="form-input" type="email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Password *</label>
                  <input className="form-input" type="password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select className="form-select" value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })}>
                    {ALL_ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input className="form-input" value={newUser.phone} onChange={e => setNewUser({ ...newUser, phone: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Designation</label>
                  <input className="form-input" value={newUser.designation} onChange={e => setNewUser({ ...newUser, designation: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Department</label>
                  <input className="form-input" value={newUser.department} onChange={e => setNewUser({ ...newUser, department: e.target.value })} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                <button className="btn btn-primary" onClick={handleAddUser} disabled={saving}>{saving ? 'Creating...' : 'Create User'}</button>
                <button className="btn btn-secondary" onClick={() => setShowAddUser(false)}>Cancel</button>
              </div>
            </div>
          )}

          {/* Edit User Modal */}
          {editingUser && (
            <div className="modal-overlay" onClick={() => setEditingUser(null)}>
              <div className="modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h3 className="modal-title">Edit User</h3>
                  <button className="modal-close" onClick={() => setEditingUser(null)}>×</button>
                </div>
                <div style={{ display: 'grid', gap: '12px' }}>
                  <div className="form-group">
                    <label className="form-label">Name</label>
                    <input className="form-input" value={editingUser.name} onChange={e => setEditingUser({ ...editingUser, name: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input className="form-input" value={editingUser.email} onChange={e => setEditingUser({ ...editingUser, email: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Role</label>
                    <select className="form-select" value={editingUser.role} onChange={e => setEditingUser({ ...editingUser, role: e.target.value })}>
                      {ALL_ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input className="form-input" value={editingUser.phone || ''} onChange={e => setEditingUser({ ...editingUser, phone: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Designation</label>
                    <input className="form-input" value={editingUser.designation || ''} onChange={e => setEditingUser({ ...editingUser, designation: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Department</label>
                    <input className="form-input" value={editingUser.department || ''} onChange={e => setEditingUser({ ...editingUser, department: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">New Password (leave blank to keep current)</label>
                    <input className="form-input" type="password" value={editingUser.password || ''} onChange={e => setEditingUser({ ...editingUser, password: e.target.value })} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'flex-end' }}>
                  <button className="btn btn-secondary" onClick={() => setEditingUser(null)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleUpdateUser} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
                </div>
              </div>
            </div>
          )}

          {/* Users Table */}
          <div className="card">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Contact</th>
                    <th>Department</th>
                    <th>Status</th>
                    <th>Last Login</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id}>
                      <td>
                        <div style={{ fontWeight: '600' }}>{u.name}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{u.email}</div>
                      </td>
                      <td>
                        <span style={{
                          padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                          background: ROLE_COLORS[u.role] + '15', color: ROLE_COLORS[u.role], border: `1px solid ${ROLE_COLORS[u.role]}30`
                        }}>
                          {ROLE_LABELS[u.role] || u.role}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontSize: '13px' }}>{u.phone || '-'}</div>
                      </td>
                      <td>
                        <div style={{ fontSize: '13px' }}>{u.designation || '-'}</div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>{u.department || ''}</div>
                      </td>
                      <td>
                        <span style={{
                          padding: '3px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: '600',
                          background: u.is_active ? '#d1fae5' : '#fee2e2', color: u.is_active ? '#065f46' : '#991b1b'
                        }}>
                          {u.is_active ? '● Active' : '● Revoked'}
                        </span>
                      </td>
                      <td style={{ fontSize: '12px', color: '#6b7280' }}>
                        {u.last_login ? new Date(u.last_login).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Never'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          <button className="btn btn-secondary" style={{ fontSize: '11px', padding: '4px 8px' }} onClick={() => setEditingUser({ ...u, password: '' })}>✏️ Edit</button>
                          <button className="btn btn-secondary" style={{ fontSize: '11px', padding: '4px 8px' }} onClick={() => handleResetPassword(u.id)}>🔑 Reset</button>
                          <button
                            className={u.is_active ? 'btn btn-danger' : 'btn btn-success'}
                            style={{ fontSize: '11px', padding: '4px 8px' }}
                            onClick={() => handleToggleActive(u.id, u.is_active)}
                          >
                            {u.is_active ? '🚫 Revoke' : '✅ Activate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ═══ TAB 2: ROLE PERMISSIONS MATRIX ═══ */}
      {activeTab === 'permissions' && (
        <div>
          <div style={{ marginBottom: '16px', padding: '12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', fontSize: '13px', color: '#92400e' }}>
            <strong>Permission Legend:</strong> 👁 View &nbsp; ➕ Create &nbsp; ✏️ Edit &nbsp; 🗑 Delete &nbsp; 📱 View Phone &nbsp; 👤 View Name &nbsp; 📥 Export
          </div>

          {ALL_ROLES.map(role => (
            <div key={role} className="card" style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{
                    padding: '6px 14px', borderRadius: '12px', fontSize: '14px', fontWeight: '700',
                    background: ROLE_COLORS[role] + '15', color: ROLE_COLORS[role], border: `1px solid ${ROLE_COLORS[role]}30`
                  }}>
                    {ROLE_LABELS[role]}
                  </span>
                  {dirtyRoles.has(role) && <span style={{ fontSize: '12px', color: '#f59e0b', fontWeight: '600' }}>● Unsaved changes</span>}
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button className="btn btn-secondary" style={{ fontSize: '11px', padding: '4px 10px' }} onClick={() => toggleAllForRole(role, true)}>Grant All</button>
                  <button className="btn btn-secondary" style={{ fontSize: '11px', padding: '4px 10px' }} onClick={() => toggleAllForRole(role, false)}>Revoke All</button>
                  {dirtyRoles.has(role) && (
                    <button className="btn btn-primary" style={{ fontSize: '11px', padding: '4px 12px' }} onClick={() => saveRolePerms(role)} disabled={saving}>
                      {saving ? '...' : '💾 Save'}
                    </button>
                  )}
                </div>
              </div>

              <div className="table-container">
                <table className="table" style={{ fontSize: '12px' }}>
                  <thead>
                    <tr>
                      <th style={{ minWidth: '140px' }}>Module</th>
                      {PERM_KEYS.map(k => (
                        <th key={k} style={{ textAlign: 'center', minWidth: '50px' }} title={PERM_TOOLTIPS[k]}>{PERM_LABELS[k]}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ALL_FEATURES.map(feature => {
                      const perms = matrix[role]?.[feature] || {};
                      return (
                        <tr key={feature}>
                          <td style={{ fontWeight: '500', textTransform: 'capitalize' }}>{feature.replace(/-/g, ' ')}</td>
                          {PERM_KEYS.map(k => (
                            <td key={k} style={{ textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={!!perms[k]}
                                onChange={() => togglePerm(role, feature, k)}
                                style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: ROLE_COLORS[role] }}
                              />
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ═══ TAB 3: AUDIT TRAIL ═══ */}
      {activeTab === 'audit' && (
        <div>
          {/* Access Logs */}
          <div className="card" style={{ marginBottom: '16px' }}>
            <h2 className="card-title">📋 Data Access Log</h2>
            {auditLogs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔒</div>
                <p>No access logs recorded yet. Logs will appear when users reveal masked data.</p>
              </div>
            ) : (
              <div className="table-container">
                <table className="table" style={{ fontSize: '13px' }}>
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>User</th>
                      <th>Action</th>
                      <th>Entity</th>
                      <th>Field Revealed</th>
                      <th>IP Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map(log => (
                      <tr key={log.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{new Date(log.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                        <td><strong>{log.user_name}</strong></td>
                        <td>
                          <span style={{
                            padding: '2px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: '600',
                            background: log.action === 'reveal' ? '#fef3c7' : '#dbeafe',
                            color: log.action === 'reveal' ? '#92400e' : '#1e40af'
                          }}>
                            {log.action}
                          </span>
                        </td>
                        <td>{log.entity_type} #{log.entity_id} <span style={{ color: '#6b7280' }}>{log.entity_name || ''}</span></td>
                        <td style={{ fontWeight: '600', color: '#dc2626' }}>{log.field_revealed || '-'}</td>
                        <td style={{ fontSize: '11px', color: '#6b7280' }}>{log.ip_address || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Login Logs */}
          <div className="card">
            <h2 className="card-title">🔑 Login History</h2>
            {loginLogs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>📝</div>
                <p>No login events recorded yet.</p>
              </div>
            ) : (
              <div className="table-container">
                <table className="table" style={{ fontSize: '13px' }}>
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>User</th>
                      <th>Role</th>
                      <th>Action</th>
                      <th>IP Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loginLogs.map((log, i) => (
                      <tr key={i}>
                        <td style={{ whiteSpace: 'nowrap' }}>{new Date(log.created_at).toLocaleString('en-IN')}</td>
                        <td><strong>{log.user_name}</strong></td>
                        <td><span style={{ padding: '2px 8px', borderRadius: '8px', fontSize: '11px', background: '#f3f4f6' }}>{log.user_role}</span></td>
                        <td>
                          <span style={{
                            padding: '2px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: '600',
                            background: log.action === 'login' ? '#d1fae5' : '#fee2e2',
                            color: log.action === 'login' ? '#065f46' : '#991b1b'
                          }}>
                            {log.action}
                          </span>
                        </td>
                        <td style={{ fontSize: '11px', color: '#6b7280' }}>{log.ip_address || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ TAB 4: LOGIN RESTRICTIONS ═══ */}
      {activeTab === 'restrictions' && (
        <div>
          <div className="card">
            <h2 className="card-title">⏰ Login Time Restrictions</h2>
            <p style={{ color: '#6b7280', marginBottom: '16px', fontSize: '13px' }}>
              Control when each role can log in to the system. Leave unrestricted for 24/7 access.
            </p>

            <div style={{ display: 'grid', gap: '16px' }}>
              {ALL_ROLES.filter(r => r !== 'super_admin').map(role => {
                const existing = loginRestrictions.find(r => r.role_name === role) || {};
                const isActive = existing.is_active || false;
                const days = (existing.allowed_days || 'mon,tue,wed,thu,fri,sat,sun').split(',');

                return (
                  <div key={role} style={{
                    padding: '16px', border: '1px solid #e5e7eb', borderRadius: '8px',
                    background: isActive ? '#fefce8' : '#f9fafb'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <span style={{
                        padding: '4px 12px', borderRadius: '10px', fontSize: '13px', fontWeight: '600',
                        background: ROLE_COLORS[role] + '15', color: ROLE_COLORS[role]
                      }}>
                        {ROLE_LABELS[role]}
                      </span>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
                        <input type="checkbox" checked={isActive}
                          onChange={e => saveRestriction(role, { ...existing, is_active: e.target.checked })}
                          style={{ width: '18px', height: '18px' }}
                        />
                        Restrict Login Hours
                      </label>
                    </div>

                    {isActive && (
                      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <label style={{ fontSize: '13px', fontWeight: '500' }}>From:</label>
                          <input type="time" value={existing.allowed_from || '09:00'} className="form-input" style={{ width: '120px' }}
                            onChange={e => saveRestriction(role, { ...existing, allowed_from: e.target.value })} />
                          <label style={{ fontSize: '13px', fontWeight: '500' }}>To:</label>
                          <input type="time" value={existing.allowed_until || '18:00'} className="form-input" style={{ width: '120px' }}
                            onChange={e => saveRestriction(role, { ...existing, allowed_until: e.target.value })} />
                        </div>

                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map(day => (
                            <button
                              key={day}
                              onClick={() => {
                                const newDays = days.includes(day) ? days.filter(d => d !== day) : [...days, day];
                                saveRestriction(role, { ...existing, allowed_days: newDays.join(',') });
                              }}
                              style={{
                                padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600',
                                border: '1px solid', cursor: 'pointer',
                                background: days.includes(day) ? '#2563eb' : '#f3f4f6',
                                color: days.includes(day) ? '#fff' : '#374151',
                                borderColor: days.includes(day) ? '#2563eb' : '#d1d5db'
                              }}
                            >
                              {day.toUpperCase()}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessControl;
